import { useEffect, useMemo, useRef } from 'react';
import { Draggable } from '@fullcalendar/interaction';
import { ArrowRight } from 'lucide-react';
import { DateTime } from 'luxon';

import { cn } from '@/lib/cn';
import { formatDuration } from '@/features/tasks/tree';
import type { Task } from '@/features/tasks/types';
import { DUE_PILL, dueInfo } from './backlogSort';
import { blockMinutes, groupCarryOver } from './carryOver';
import type { TimeBlock } from './types';

interface Props {
  blocks: TimeBlock[];
  taskById: Map<number, Task>;
  timezone: string;
  /** The day the grid is showing, where "Move" sends things. */
  targetLabel: string;
  clientColor: (clientId: number) => string;
  onMove: (block: TimeBlock) => void;
  onSelectTask: (task: Task) => void;
  selectedTaskId: number | null;
}

/**
 * Work planned for an earlier day that never got finished.
 *
 * Without this the day you just failed to get through is simply gone: its
 * tasks read as "scheduled", so they are not in the backlog, and the grid only
 * shows the day you are looking at. The leftovers end up nowhere. Here they
 * are, above the backlog, with the same two ways out as everything else,
 * drag onto the grid, or one click to the same hour on the planned day.
 */
export function PlanCarryOver({
  blocks,
  taskById,
  timezone,
  targetLabel,
  clientColor,
  onMove,
  onSelectTask,
  selectedTaskId,
}: Props) {
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const draggable = new Draggable(el, {
      itemSelector: '[data-carry-chip]',
      eventData(eventEl) {
        return {
          title: eventEl.dataset.title ?? 'Task',
          duration: { minutes: Number(eventEl.dataset.minutes) || 30 },
          create: false,
        };
      },
    });
    return () => draggable.destroy();
  }, []);

  const groups = useMemo(
    () => groupCarryOver(blocks, { timezone }),
    [blocks, timezone],
  );

  if (groups.length === 0) return null;

  return (
    <div ref={listRef} className="flex flex-col gap-3">
      <div>
        <h2 className="text-small font-medium text-warning">
          Unfinished · {blocks.length}
        </h2>
        <p className="mt-0.5 text-small leading-snug text-text-tertiary">
          Planned earlier and never finished. Drag onto the grid, or move it to{' '}
          {targetLabel}.
        </p>
      </div>

      {groups.map((group) => (
        <section key={group.key} aria-label={`Unfinished from ${group.label}`}>
          <h3
            className={cn(
              'mb-1.5 flex items-baseline justify-between gap-2 text-label uppercase',
              group.stale ? 'text-danger' : 'text-text-secondary',
            )}
          >
            <span className="truncate">{group.label}</span>
            <span className="shrink-0 tabular font-normal text-text-tertiary">
              {group.meta}
            </span>
          </h3>

          <ul className="flex flex-col gap-1.5">
            {group.blocks.map((block) => {
              const task = taskById.get(block.task_id) ?? null;
              const minutes = blockMinutes(block);
              const duration = formatDuration(minutes) ?? `${minutes}m`;
              const was = DateTime.fromISO(block.starts_at)
                .setZone(timezone)
                .toFormat('h:mm a');
              const due = dueInfo(task?.due_at ?? null, timezone);
              const selected = task != null && selectedTaskId === task.id;
              return (
                <li
                  key={block.id}
                  // Border on the wrapper, not the chip: the move action is a
                  // real button, and a button nested inside a role="button"
                  // element is not something a screen reader can describe.
                  className={cn(
                    'rounded border border-dashed border-border bg-surface-1',
                    'border-l-[3px]',
                    selected && 'ring-2 ring-ring',
                  )}
                  style={{
                    borderLeftColor: clientColor(block.client_id),
                    borderLeftStyle: 'solid',
                  }}
                >
                  <div
                    data-carry-chip
                    data-block-id={block.id}
                    data-task-id={block.task_id}
                    data-title={block.task_title}
                    data-minutes={minutes}
                    tabIndex={0}
                    role="button"
                    aria-pressed={selected}
                    aria-label={`${block.task_title}, ${duration}, planned ${group.label} at ${was}${
                      due.at ? `, ${due.full}` : ''
                    }. Click for details, drag onto the calendar, or press M to move it to ${targetLabel}.`}
                    onClick={() => task && onSelectTask(task)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        if (task) onSelectTask(task);
                      }
                      if (e.key === 'm' || e.key === 'M') {
                        e.preventDefault();
                        onMove(block);
                      }
                    }}
                    className={cn(
                      'plan-task-chip block cursor-grab rounded-t px-3 pt-2',
                      'outline-none focus-visible:ring-2 focus-visible:ring-ring active:cursor-grabbing',
                    )}
                  >
                    <p className="truncate text-small font-medium leading-tight text-text">
                      {block.task_title}
                    </p>
                    <p className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-small leading-none tabular text-text-tertiary">
                      <span>{duration}</span>
                      <span>was {was}</span>
                      {due.at && (
                        <span
                          title={due.full}
                          className={cn(
                            'rounded-sm px-1.5 py-0.5 font-medium',
                            DUE_PILL[due.tone],
                          )}
                        >
                          {due.label}
                        </span>
                      )}
                    </p>
                  </div>

                  <div className="px-2 pb-1.5 pt-1">
                    <button
                      type="button"
                      onClick={() => onMove(block)}
                      className={cn(
                        'pressable inline-flex min-h-control items-center gap-1 rounded',
                        'px-2 text-small font-medium text-accent',
                        'transition-colors duration-press hover:bg-accent-quiet',
                        'outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      )}
                    >
                      <ArrowRight className="size-icon shrink-0" aria-hidden />
                      Move to {targetLabel}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}
